<template>
    <div id="app" :data-theme="localTheme" :class="`photo-claim page-${$route.path.substring(1, $route.path.length)}`">

      <header class="site-header">
        <div class="flexbox-shim"></div>
        <router-link to="/">
          <img :alt="logoAltText" :src="logo" :height="logoHeight" />
        </router-link>
      </header>
      <div class="site-message-area">
        <message></message>
      </div>
      <main class="site-main">
        <router-view></router-view>
      </main>
    </div>
</template>

<script>
import moment from 'moment';
import appMixins from '@/mixins/appMixins';
import veeValidateConfig from '@/mixins/veeValidateConfig';
import localStoragePolyfill from '@/utils/localStoragePolyfill';
import logoFrank from '@/assets/site-logo-frank.png';
import logoGMHBA from '@/assets/site-logo-gmhba.png';
import Message from '@/components/global/Message';
import iOSPrompt from '@/components/global/iOSPrompt';

export default {
  components: {
    Message,
  },
  name: 'app',
  mixins: [appMixins, veeValidateConfig],

  data() {
    return {
      errors: [],
      showMessage: false,
      showDev: true,
      theme: this.$appsettings.Theme,
      localTheme: this.$appsettings.Theme.toLowerCase(),
      menuIsOpen: false,
    };
  },

  created() {
    // Load localStoragePolyfill
    localStoragePolyfill();

    const self = this;

    // Increment visits count
    window.photoClaim.visits += 1;

    // If user enters from an icon that's saved to their home screen, set a var
    if (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone
    ) {
      window.photoClaim.isFromHomeScreen = true;
    }

    let offlineEventCount = 0;

    // Add a message to inform the user when they are offline.
    function updateOnlineStatus(event) {
      const networkStatus = navigator.onLine ? 'online' : 'offline';

      switch (networkStatus) {
        case 'offline':
          self.emitMessageEvent('error', 'You are currently offline.');
          offlineEventCount += 1;
          break;
        case 'online':
          if (offlineEventCount >= 1) {
            self.emitMessageEvent('success', 'You are now online.');
          }
          break;
        default:
      }
    }

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
  },

  mounted() {
    // When we are in the period where we want to show a maintenance message,
    // show it.

    const maintenanceMessageBegins = moment('05/12/19 00:00:00', 'DD/MM/YY HH:mm:ss');

    // stop showing the message when the maintenance period starts, as the site will be down.
    // once it's back up, we no longer want to show the maintenance message.
    const maintenanceMessageEnds = moment('08/12/19 06:00:00', 'DD/MM/YY HH:mm:ss');

    // This can handle a single date, or multiple dates.
    // eg. 'on Monday, 1st of January'
    // or 'from Tuesday, 2nd of March to Friday, 5th of March'
    const unavailableDateWindow = 'on Sunday, 8th of December';

    // display unavailable start time
    const unavailableTimeStart = '6:00 am';

    // display unavailable end time
    const unavailableTimeEnd = '11:00 pm';

    if (
        moment().isAfter(maintenanceMessageBegins) &&
        moment().isBefore(maintenanceMessageEnds)
      ) {
      this.emitMessageEvent('inverse', `<strong>Notice</strong><br />Photo claiming will be unavailable ${unavailableDateWindow} from ${unavailableTimeStart} to ${unavailableTimeEnd}, due to scheduled maintenance. We apologise for any inconvenience caused.`);
    }
  },

  computed: {
    // return appropriate Brand logo
    logo() {
      switch (this.localTheme) {
        case 'frank':
          return logoFrank;
          break;
        case 'gmhba':
          return logoGMHBA;
          break;
        default:
          return '';
          break;
      }
    },

    logoAltText() {
      return `${this.theme} Photo Claim App`;
    },

    logoHeight() {
      const frankLogoHeight = 42;
      const gmhbaLogoHeight = 19;
      return this.localTheme === 'gmhba' ? gmhbaLogoHeight : frankLogoHeight;
    },
  },

  methods: {

    customCloseMenu() {
      this.menuIsOpen = false;
    },
  },

  watch: {
    showIOSPrompt() {
      // When showIOSPrompt prop is first changed, set hasSeenIOSPrompt flag to true
      window.photoClaim.hasSeenIOSPrompt = true;
    }
  },
};
</script>

<style lang="scss">

:root {

  // Global colors
  --color-light:      #FFF;

  --color-grey-100:   #2C3E50;
  --color-grey-600:   #E4E4E4;
  --color-grey-800:   #CCCCCC;
  --color-grey-900:   #F3F1F3;

  --color-dark:       #000;

  --color-text:         var(--color-dark-grey);
  --color-error:      #de1b1b;
  --color-success:    #7ED321;

  // Typography
  --text-size-01: 1.625rem;  // 26px / 16px
  --text-size-02: 1.375rem;  // 22px / 16px
  --text-size-03: 1.125rem;  // 18px / 16px
  --text-size-04: 1rem;      // 16px / 16px
  --text-size-05: .875rem;   // 14px / 16px
  --text-size-06: .75rem;    // 12px / 16px

  // Typography - utils
  --text-body: var(--text-size-04);
  --text-small: var(--text-size-06);

  // BRAND VARIABLES

  // -- Colors

  // GMHBA
  //////////////////////////////////////////////////////////////////////////////

  --color-gmhba-primary: #0B7DBB;
  --color-gmhba-primary-dark: #0A5B87;
  --color-gmhba-primary-light: #1481bb;

  --font-gmhba: 'Open Sans', Helvetica, Arial, sans-serif;

  // FRANK
  //////////////////////////////////////////////////////////////////////////////

  --color-frank-primary: #8FE2B0;
  --color-frank-secondary: #000;
  --color-frank-secondary-light: #3b3b3b;
  --color-frank-hover: #7462e0;
  --font-frank: 'Montserrat', sans-serif;
}

html {
  box-sizing: border-box;
  height: 100%;
  margin: 0;
  padding: 0;
}

*, *:before, *:after {
  box-sizing: inherit;
}

body {
  color: var(--color-text);
  height: 100%;
  margin: 0;
  padding: 0;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-family: sans-serif;
}

[data-theme="gmhba"] {
    font-family: var(--font-gmhba);
}

[data-theme="frank"] {
  font-family: var(--font-frank);
}

p {
  font-size: var(--text-body);
  line-height: 1.4;
  margin-top: 0;
}

// Typography
////////////////////////////////////////////////////////////////////////////////

.h1,
%h1 {
  font-size: var(--text-size-01);
}

.h2,
%h2 {
  font-size: var(--text-size-02);
}

.h3,
%h3 {
  font-size: var(--text-size-03);
}

.h4,
%h4 {
  font-size: var(--text-size-04);
}

.h5,
%h5 {
  font-size: var(--text-size-05);
}

.h6,
%h6 {
  font-size: var(--text-size-06);
}

.label {
  display: inline-block;
  font-weight: 700;
  margin-bottom: 2px;

  &--small {
    font-size: .8rem;
  }
}

#app {
  min-height: 100%;
}

.photo-claim {
  &[data-theme="gmhba"] {
    background: var(--color-grey-900);
  }
}

.site-main {
  color: var(--color-dark-grey);
  margin: 0 auto;
  max-width: 400px;
  padding: 0 20px 20px;

  // Place a colored block as a background at the top of site-main
  &:before {
    content: '';
    display: block;
    width: 100%;
    height: 160px;
    z-index: -1;
    left: 0;
    position: absolute;
    background: transparent;

    @include theme-gmhba {
      background-color: transparent;
    }

    @include theme-frank {
      background-color: var(--color-frank-primary);
    }
  }
}

[data-theme="frank"].page-claim-rules .site-main:before,
[data-theme="frank"].page-terms .site-main:before {
  display: none;
}

.text-center {
  text-align: center;
}

.site-header {
  background: white;
  display: flex;
  align-items: center;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  padding: 10px;
  position: relative;
  text-align: center;
  z-index: 200;

  @include theme-gmhba {
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
  }

  @include theme-frank {
    background: var(--color-frank-primary);
  }
}

.page-body {
  background-color: var(--color-light);
  padding: 1rem;
}

a {

  @include theme-gmhba {
    text-decoration: none;
    color: var(--color-gmhba-primary);

    &:hover,
    &:active {
      text-decoration: underline;
    }
  }

  @include theme-frank {
    text-decoration: underline;
    color: var(--color-frank-secondary);

    &:not(.button):hover,
    &:not(.button):active  {
      color: var(--color-frank-hover);
    }


  }


}

// Buttons
////////////////////////////////////////////////////////////////////////////////

.button {
  font-size: var(--text-size-05);
  cursor: pointer;
  display: block;
  font-weight: 700;
  text-align: center;
  min-height: 40px;
  line-height: 2.5rem;
  margin: 0 auto;
  text-transform: uppercase;
  width: 100%;
  max-width: 280px;
  appearance: none;
  transition: background-color 200ms ease;
  border-radius: 0;

  @include theme-all {
    color: var(--color-light);
  }

  @include theme-gmhba {
    background-color: var(--color-gmhba-primary);
    border: 2px solid var(--color-gmhba-primary);
    font-family: var(--font-gmhba);
  }

  @include theme-frank {
    background-color: var(--color-frank-secondary);
    border: 2px solid var(--color-frank-secondary);
    font-family: var(--font-frank);
  }

  &:hover {
    background-color: var(--color-gmhba-primary-dark);
    border-color: var(--color-gmhba-primary-dark);
    color: var(--color-light);
    transition: background-color 200ms ease;

    @include theme-frank {
      background-color: var(--color-frank-secondary-light);
    }
  }

  // Disabled button
  &[disabled="disabled"] {
    @include theme-gmhba {
      background: var(--color-gmhba-primary-light);
    }

    @include theme-frank {
      background: var(--color-frank-secondary-light);

      &.button--outline,
      &.button--text,
      &.button--link {
        background: transparent;
      }
    }
  }

  // Variant: button--outline
  &--outline {
    @include theme-all {
      background: transparent;
    }

    @include theme-gmhba {
      color: var(--color-gmhba-primary);
    }

    @include theme-frank {
      color: var(--color-frank-secondary);
    }

    &:hover {
      color: var(--color-light);

      @include theme-gmhba {
        color: var(--color-gmhba-primary-dark);
      }
    }
  }

  // Variant: button--text
  &--text {
    @include theme-all {
      border: 0;
      background: transparent;

      &:hover {
        background: transparent;
        text-decoration: underline;
      }
    }

    @include theme-gmhba {
      color: var(--color-gmhba-primary);
    }

    @include theme-frank {
      color: var(--color-frank-secondary);
    }
  }

  // Button: display inline
  &--inline {
    display: inline;
    width: auto;
    min-height: 0;
    line-height: 0;
    margin: unset;
  }

  &--link {
    text-transform: none;
    text-decoration: underline;
    font-weight: 400;

    @include theme-all {
      background: transparent;
      border: 0;
    }

    @include theme-gmhba {
      color: var(--color-gmhba-primary);
    }

    @include theme-frank {
      color: var(--color-frank-secondary);

      &:hover {
        background: transparent;
      }
    }

  }
}

////////////////////////////////////////////////////////////////////////////////

// Button Group
// Used to layout 2 or more buttons in a group
////////////////////////////////////////////////////////////////////////////////

.button-group {
  display: grid;
  grid-gap: 10px;
  gap: 10px;

  &--two-columns {
    grid-template-columns: 1fr 1fr;
  }

  &--two-rows {
    grid-template-rows: 1fr 1fr;
  }

  .button {
    max-width: none;
  }
}

.hero-banner {
  display: block;
  margin: 0 -20px 20px;
  width: calc(100% + 40px);
}

.flexbox-shim {
  width: 31px;
}

input[type="text"],
input[type="email"],
input[type="number"] {
  background-color: transparent;
  box-shadow: none;
  border: none;
  border-radius: 0;
  border-bottom: 1px solid black;
  font-size: 1rem;
  line-height: 2rem;
  margin: 0 0 .5rem;
  min-height: 40px;
  padding: 0;
}

::-webkit-input-placeholder { /* Chrome/Opera/Safari */
  font-style: italic;
}
::-moz-placeholder { /* Firefox 19+ */
  font-style: italic;
}
:-ms-input-placeholder { /* IE 10+ */
  font-style: italic;
}
:-moz-placeholder { /* Firefox 18- */
  font-style: italic;
}

.visuallyhidden {
  /* https://snook.ca/archives/html_and_css/hiding-content-for-accessibility */
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px 1px 1px 1px); /* IE6, IE7 */
  clip: rect(1px, 1px, 1px, 1px);
}


input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.menu-toggle {
  background: transparent;
  border: none;

  &:hover {
    cursor: pointer;
  }

  svg,
  path {
    @include theme-frank {
      color: var(--color-frank-secondary);
      fill: var(--color-frank-secondary);
    }

    @include theme-gmhba {
      color: var(--color-gmhba-primary);
      fill: var(--color-gmhba-primary);
    }
  }

  &--close {
    padding: 0;
  }

  &--open svg {
    display: block;
    width: 23px;
    height: 23px;
  }
}

.off-canvas-menu {
  background: white;
  box-shadow: 0 0 16px rgba(0, 0, 0, 0.3);
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  transform: translate3d(-340px, 0, 0);
  transition: transform 300ms ease-in-out;
  width: 280px;
  z-index: 500;

  .menu-open & {
    transform: translate3d(0, 0, 0);
    transition: transform 300ms ease-in-out;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    align-items: center;
    border-bottom: 1px solid var(--color-grey-800);
    display: flex;
    justify-content: space-around;
    min-height: 70px;

    a {
      color: var(--color-text);
      display: block;
      font-size: 16px;
      text-decoration: none;
      width: 100%;
      padding-left: 20px;
      font-weight: 700;

      &.router-link-exact-active {
        text-decoration: underline;

        @include theme-gmhba {
          color: var(--color-gmhba-primary);
        }

        @include theme-frank {
          color: var(--color-frank-hover);
        }
      }
    }
  }

  &__header {
    border-bottom: 1px solid var(--color-grey-800);
    display: flex;
    justify-content: space-between;
    padding: 12px 20px 8px;

    @include theme-frank {
      background-color: var(--color-frank-primary);
      border-bottom: 0;
    }
  }
}

.menu-backdrop {
  background: rgba(0, 0, 0, 0.7);
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  display: none;
  z-index: 400;

  .menu-open & {
    display: block;
  }
}

</style>



// WEBPACK FOOTER //
// src/App.vue